import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { PrivatePageLayout, PageHeader } from '../../components/PrivatePage';
import Toast from '../../components/Toast/Toast';
import { AppButton } from '../../components/AppButton/AppButton';
import { getSettings, updateSettings, getProfilePhotoUrl } from '../../services/SettingsService';
import { useGlobalContext } from '../../UserContext/UserContext';
import './Settings.css';

const MAX_PHOTO_BYTES = 2 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

function getInitials(name, email) {
    const source = (name || email || '').trim();
    if (!source) return '?';

    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return source.slice(0, 2).toUpperCase();
}

function SettingsField({ id, label, type = 'text', value, onChange, placeholder, disabled, hint, children }) {
    return (
        <div className="settings-field">
            <label htmlFor={id}>
                {label}
                {children}
            </label>
            <input
                id={id}
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                disabled={disabled}
            />
            {hint && <p className="settings-field-hint">{hint}</p>}
        </div>
    );
}

function SettingsCard({ icon, title, description, children, className = '' }) {
    return (
        <section className={`settings-card ${className}`.trim()}>
            <div className="settings-card-header">
                <span className="settings-card-icon" aria-hidden="true">
                    <Icon icon={icon} width={20} height={20} />
                </span>
                <div>
                    <h2 className="settings-card-title">{title}</h2>
                    {description && <p className="settings-card-text">{description}</p>}
                </div>
            </div>
            {children}
        </section>
    );
}

function Settings() {
    const { refreshProfile } = useGlobalContext();
    const confirmPassword = useRef('');
    const photoInputRef = useRef(null);
    const [settings, setSettings] = useState({});
    const [notification, setNotification] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [pendingPhotoData, setPendingPhotoData] = useState(null);
    const [removePhoto, setRemovePhoto] = useState(false);

    useEffect(() => {
        getSettings()
            .then((result) => {
                setSettings(result);
                setPhotoPreview(getProfilePhotoUrl(result.profilePhoto));
            })
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            })
            .finally(() => setIsLoading(false));
    }, []);

    function onInputChange(event) {
        setSettings((prevState) => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    function onPhotoSelect(event) {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) return;

        if (!ALLOWED_PHOTO_TYPES.has(file.type)) {
            return setNotification({
                type: 'error',
                text: 'Profile photo must be a JPEG, PNG, or WebP image.',
            });
        }

        if (file.size > MAX_PHOTO_BYTES) {
            return setNotification({
                type: 'error',
                text: 'Profile photo must be 2 MB or smaller.',
            });
        }

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result;
            setPendingPhotoData(dataUrl);
            setPhotoPreview(dataUrl);
            setRemovePhoto(false);
        };
        reader.onerror = () => {
            setNotification({ type: 'error', text: 'Could not read the selected image.' });
        };
        reader.readAsDataURL(file);
    }

    function onRemovePhoto() {
        setPendingPhotoData(null);
        setPhotoPreview(null);
        setRemovePhoto(true);
        if (photoInputRef.current) {
            photoInputRef.current.value = '';
        }
    }

    function onFormSubmit() {
        if (
            (settings.password || confirmPassword.current.value)
            && settings.password !== confirmPassword.current.value
        ) {
            return setNotification({
                type: 'error',
                text: 'New Password and Confirm Password must match.',
            });
        }

        const payload = { ...settings };
        if (pendingPhotoData) {
            payload.profilePhotoData = pendingPhotoData;
        } else if (removePhoto) {
            payload.removeProfilePhoto = true;
        }

        setIsSaving(true);
        updateSettings(payload)
            .then((result) => {
                if (result) {
                    setNotification({ type: 'success', text: 'Settings saved successfully.' });
                    confirmPassword.current.value = '';
                    setSettings((prev) => ({
                        ...prev,
                        ...result,
                        password: '',
                    }));
                    setPendingPhotoData(null);
                    setRemovePhoto(false);
                    setPhotoPreview(getProfilePhotoUrl(result.profilePhoto));
                    if (refreshProfile) refreshProfile();
                } else {
                    setNotification({ type: 'error', text: result });
                }
            })
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            })
            .finally(() => setIsSaving(false));
    }

    const hasFutures = settings?.limit?.hasFutures;
    const displayName = (settings.name || '').trim() || 'Your profile';
    const initials = getInitials(settings.name, settings.email);
    const hasStoredPhoto = Boolean(settings.profilePhoto);
    const showRemovePhoto = Boolean(photoPreview) && (hasStoredPhoto || pendingPhotoData);

    return (
        <React.Fragment>
            <PrivatePageLayout width="narrow" className="settings-page">
                        <PageHeader
                            eyebrow="Account"
                            title="Settings"
                            subtitle="Manage your profile, notification preferences, and exchange API keys. Keys are stored securely and used only for trade execution."
                        />

                        {isLoading ? (
                            <div aria-label="Loading settings">
                                <div className="settings-skeleton settings-skeleton--title" />
                                <div className="settings-skeleton settings-skeleton--card" />
                                <div className="settings-skeleton settings-skeleton--card" />
                            </div>
                        ) : (
                            <>
                                <div className="settings-grid">
                                    <SettingsCard
                                        icon="mdi:account-outline"
                                        title="Profile"
                                        description="Your name, photo, and contact details for your NeuraTrader account."
                                    >
                                        <div className="settings-profile">
                                            <div className="settings-profile-avatar" aria-hidden="true">
                                                {photoPreview ? (
                                                    <img src={photoPreview} alt="" />
                                                ) : (
                                                    <span>{initials}</span>
                                                )}
                                            </div>
                                            <div className="settings-profile-meta">
                                                <p className="settings-profile-name">{displayName}</p>
                                                {settings.email && (
                                                    <p className="settings-profile-email">{settings.email}</p>
                                                )}
                                                <div className="settings-profile-actions">
                                                    <input
                                                        ref={photoInputRef}
                                                        id="profilePhoto"
                                                        type="file"
                                                        accept="image/jpeg,image/png,image/webp"
                                                        className="settings-profile-file-input"
                                                        onChange={onPhotoSelect}
                                                    />
                                                    <AppButton
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => photoInputRef.current?.click()}
                                                    >
                                                        {photoPreview ? 'Change photo' : 'Upload photo'}
                                                    </AppButton>
                                                    {showRemovePhoto && (
                                                        <AppButton
                                                            variant="secondary"
                                                            size="sm"
                                                            onClick={onRemovePhoto}
                                                        >
                                                            Remove
                                                        </AppButton>
                                                    )}
                                                </div>
                                                <p className="settings-field-hint settings-profile-hint">
                                                    JPEG, PNG, or WebP. Max 2 MB. Save changes to apply.
                                                </p>
                                            </div>
                                        </div>
                                        <SettingsField
                                            id="name"
                                            label="Full name"
                                            type="text"
                                            placeholder="Your full name"
                                            value={settings.name || ''}
                                            onChange={onInputChange}
                                        />
                                        <SettingsField
                                            id="email"
                                            label="Email"
                                            type="email"
                                            placeholder="name@company.com"
                                            value={settings.email || ''}
                                            onChange={onInputChange}
                                        />
                                        <SettingsField
                                            id="phone"
                                            label="Cellphone"
                                            type="text"
                                            placeholder="+1 555 123 4567"
                                            value={settings.phone || ''}
                                            onChange={onInputChange}
                                            hint="Optional. Used for account alerts if SMS is enabled."
                                        />
                                    </SettingsCard>

                                    <SettingsCard
                                        icon="mdi:layers-outline"
                                        title="Plan & alerts"
                                        description="Your subscription tier and Telegram notifications."
                                    >
                                        <div className="settings-field">
                                            <label htmlFor="limit">Current plan</label>
                                            <div className="settings-plan-badge" id="limit">
                                                <Icon icon="mdi:shield-check-outline" width={18} height={18} />
                                                {settings.limit ? settings.limit.name : 'Free'}
                                            </div>
                                            <Link to="/subscription" className="settings-upgrade-link">
                                                View plans & upgrade →
                                            </Link>
                                        </div>
                                        <SettingsField
                                            id="telegramChat"
                                            label="Telegram chat ID"
                                            type="text"
                                            placeholder="Enter your Telegram chat ID"
                                            value={settings.telegramChat || ''}
                                            onChange={onInputChange}
                                            hint="Receive bot alerts in Telegram when automations fire."
                                        >
                                            {settings.telegramBot && (
                                                <a
                                                    href={`https://t.me/${settings.telegramBot}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="settings-help-link"
                                                    title="Open Telegram bot"
                                                    aria-label="Telegram setup help"
                                                >
                                                    ?
                                                </a>
                                            )}
                                        </SettingsField>
                                    </SettingsCard>
                                </div>

                                <SettingsCard
                                    icon="mdi:lock-outline"
                                    title="Security"
                                    description="Update your password. Leave blank to keep your current password."
                                >
                                    <div className="settings-grid">
                                        <SettingsField
                                            id="password"
                                            label="New password"
                                            type="password"
                                            placeholder="Enter a new password"
                                            value={settings.password || ''}
                                            onChange={onInputChange}
                                        />
                                        <div className="settings-field">
                                            <label htmlFor="confirmPassword">Confirm password</label>
                                            <input
                                                ref={confirmPassword}
                                                id="confirmPassword"
                                                type="password"
                                                placeholder="Repeat your new password"
                                            />
                                        </div>
                                    </div>
                                </SettingsCard>

                                <SettingsCard
                                    icon="mdi:key-outline"
                                    title="Exchange API keys"
                                    description="Connect your Binance account. Use trade-only permissions — never enable withdrawals."
                                >
                                    <div className="settings-notice">
                                        <Icon icon="mdi:information-outline" className="settings-notice-icon" aria-hidden="true" />
                                        <span>
                                            API keys are encrypted and never shared. Spot keys are required for all bots;
                                            {hasFutures ? ' futures keys unlock perpetual trading on your plan.' : ' upgrade your plan to add futures keys.'}
                                        </span>
                                    </div>

                                    <div className="settings-grid settings-grid--single">
                                        <SettingsField
                                            id="accessKey"
                                            label="Spot access key"
                                            type="text"
                                            placeholder="Enter your Spot API access key"
                                            value={settings.accessKey || ''}
                                            onChange={onInputChange}
                                        />
                                        <SettingsField
                                            id="secretKey"
                                            label="Spot secret key"
                                            type="password"
                                            placeholder="Enter your Spot API secret key"
                                            value={settings.secretKey || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>

                                    {hasFutures && (
                                        <div className="settings-grid settings-grid--single settings-section-spaced">
                                            <SettingsField
                                                id="futuresKey"
                                                label="Futures access key"
                                                type="text"
                                                placeholder="Enter your Futures API access key"
                                                value={settings.futuresKey || ''}
                                                onChange={onInputChange}
                                            />
                                            <SettingsField
                                                id="futuresSecret"
                                                label="Futures secret key"
                                                type="password"
                                                placeholder="Enter your Futures API secret key"
                                                value={settings.futuresSecret || ''}
                                                onChange={onInputChange}
                                            />
                                        </div>
                                    )}
                                </SettingsCard>

                                <div className="settings-actions">
                                    <AppButton
                                        variant="primary"
                                        size="md"
                                        onClick={onFormSubmit}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Saving…' : 'Save changes'}
                                    </AppButton>
                                </div>
                            </>
                        )}
            </PrivatePageLayout>
            <Toast text={notification.text} type={notification.type} />
        </React.Fragment>
    );
}

export default Settings;